/**
 * Subscription Service
 *
 * Microservice for managing subscription CRUD operations
 * Handles all database interactions for subscriptions
 */

import { supabase, getCurrentUserId } from './supabaseClient';

/**
 * Get all subscriptions for the current user
 * @returns {Promise<Array>} Array of subscription objects
 */
export const getAllSubscriptions = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        category:categories(id, name, emoji, color),
        payment_method:payment_methods(id, name, type, last_four)
      `)
      .eq('user_id', userId)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }

    // Transform data to match frontend format
    return data.map(transformSubscriptionFromDB);
  } catch (error) {
    console.error('getAllSubscriptions error:', error);
    throw error;
  }
};

/**
 * Get active subscriptions only
 * @returns {Promise<Array>} Array of active subscriptions
 */
export const getActiveSubscriptions = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        category:categories(id, name, emoji, color),
        payment_method:payment_methods(id, name, type, last_four)
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching active subscriptions:', error);
      throw error;
    }

    return data.map(transformSubscriptionFromDB);
  } catch (error) {
    console.error('getActiveSubscriptions error:', error);
    throw error;
  }
};

/**
 * Get paused subscriptions
 * @returns {Promise<Array>} Array of paused subscriptions
 */
export const getPausedSubscriptions = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'paused')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching paused subscriptions:', error);
      throw error;
    }

    return data.map(transformSubscriptionFromDB);
  } catch (error) {
    console.error('getPausedSubscriptions error:', error);
    throw error;
  }
};

/**
 * Get cancelled subscriptions
 * @returns {Promise<Array>} Array of cancelled subscriptions
 */
export const getCancelledSubscriptions = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'cancelled')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching cancelled subscriptions:', error);
      throw error;
    }

    return data.map(transformSubscriptionFromDB);
  } catch (error) {
    console.error('getCancelledSubscriptions error:', error);
    throw error;
  }
};

/**
 * Get a single subscription by ID
 * @param {string} subscriptionId - The subscription ID
 * @returns {Promise<Object>} Subscription object
 */
export const getSubscriptionById = async (subscriptionId) => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        category:categories(id, name, emoji, color),
        payment_method:payment_methods(id, name, type, last_four)
      `)
      .eq('id', subscriptionId)
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }

    return transformSubscriptionFromDB(data);
  } catch (error) {
    console.error('getSubscriptionById error:', error);
    throw error;
  }
};

/**
 * Create a new subscription
 * @param {Object} subscription - Subscription data
 * @returns {Promise<Object>} Created subscription
 */
export const createSubscription = async (subscription) => {
  try {
    const userId = await getCurrentUserId();

    // Get category ID if category name is provided
    let categoryId = subscription.category_id;
    if (subscription.category && !categoryId) {
      const { data: category } = await supabase
        .from('categories')
        .select('id')
        .eq('name', subscription.category)
        .single();

      categoryId = category?.id;
    }

    const subscriptionData = {
      user_id: userId,
      category_id: categoryId,
      payment_method_id: subscription.payment_method_id || null,
      name: subscription.name,
      service: subscription.service || subscription.name,
      description: subscription.description || null,
      logo: subscription.logo || '📋',
      website_url: subscription.url || subscription.website_url || null,
      price: parseFloat(subscription.price),
      currency: subscription.currency || 'USD',
      billing_cycle: subscription.billingCycle || subscription.billing_cycle || 'monthly',
      status: subscription.status || 'active',
      subscription_date: subscription.subscriptionDate || subscription.subscription_date || new Date().toISOString().split('T')[0],
      last_billing_date: subscription.lastBillDate || subscription.last_billing_date || null,
      next_billing_date: subscription.nextBillingDate || subscription.next_billing_date || null,
      is_shared: subscription.isShared || subscription.is_shared || false,
      shared_with: subscription.sharedWith || subscription.shared_with || [],
      tags: subscription.tags || [],
      notes: subscription.notes || null
    };

    const { data, error } = await supabase
      .from('subscriptions')
      .insert([subscriptionData])
      .select(`
        *,
        category:categories(id, name, emoji, color),
        payment_method:payment_methods(id, name, type, last_four)
      `)
      .single();

    if (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }

    return transformSubscriptionFromDB(data);
  } catch (error) {
    console.error('createSubscription error:', error);
    throw error;
  }
};

/**
 * Update an existing subscription
 * @param {string} subscriptionId - The subscription ID
 * @param {Object} updates - Updated subscription data
 * @returns {Promise<Object>} Updated subscription
 */
export const updateSubscription = async (subscriptionId, updates) => {
  try {
    const userId = await getCurrentUserId();

    // Get category ID if category name is provided
    let categoryId = updates.category_id;
    if (updates.category && !categoryId) {
      const { data: category } = await supabase
        .from('categories')
        .select('id')
        .eq('name', updates.category)
        .single();

      categoryId = category?.id;
    }

    const updateData = {
      category_id: categoryId,
      payment_method_id: updates.payment_method_id,
      name: updates.name,
      service: updates.service,
      description: updates.description,
      logo: updates.logo,
      website_url: updates.url || updates.website_url,
      price: updates.price ? parseFloat(updates.price) : undefined,
      currency: updates.currency,
      billing_cycle: updates.billingCycle || updates.billing_cycle,
      status: updates.status,
      last_billing_date: updates.lastBillDate || updates.last_billing_date,
      next_billing_date: updates.nextBillingDate || updates.next_billing_date,
      cancellation_date: updates.cancellationDate || updates.cancellation_date,
      is_shared: updates.isShared || updates.is_shared,
      shared_with: updates.sharedWith || updates.shared_with,
      tags: updates.tags,
      notes: updates.notes
    };

    // Remove undefined values
    Object.keys(updateData).forEach(key =>
      updateData[key] === undefined && delete updateData[key]
    );

    const { data, error } = await supabase
      .from('subscriptions')
      .update(updateData)
      .eq('id', subscriptionId)
      .eq('user_id', userId)
      .select(`
        *,
        category:categories(id, name, emoji, color),
        payment_method:payment_methods(id, name, type, last_four)
      `)
      .single();

    if (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }

    // IF STATUS CHANGED, LOG IT LOCALLY TO TIMELINE HISTORY
    if (updates.status) {
      // Best-effort chronological tracking for timeline mathematics
      let eventDate = updates.cancellation_date || new Date().toISOString();

      const eventRecord = {
        subscription_id: subscriptionId,
        user_id: userId,
        event_type: updates.status === 'cancelled' ? 'cancellation' :
          updates.status === 'paused' ? 'paused' :
            updates.status === 'active' ? 'reactivated' : updates.status,
        event_date: eventDate,
        price: data.price
      };

      const { error: eventError } = await supabase
        .from('subscription_events')
        .insert([eventRecord]);

      if (eventError) {
        // Just log it, don't crash the UI if timeline math tracking fails
        console.warn('Failed to chronologically log status change event:', eventError);
      }
    }

    return transformSubscriptionFromDB(data);
  } catch (error) {
    console.error('updateSubscription error:', error);
    throw error;
  }
};

/**
 * Delete a subscription
 * @param {string} subscriptionId - The subscription ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteSubscription = async (subscriptionId) => {
  try {
    const userId = await getCurrentUserId();

    const { error } = await supabase
      .from('subscriptions')
      .delete()
      .eq('id', subscriptionId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting subscription:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('deleteSubscription error:', error);
    throw error;
  }
};

/**
 * Get upcoming renewals for the user
 * @param {number} daysAhead - Number of days to look ahead (default: 30)
 * @returns {Promise<Array>} Array of upcoming subscriptions
 */
export const getUpcomingRenewals = async (daysAhead = 30) => {
  try {
    const userId = await getCurrentUserId();
    const today = new Date();
    const futureDate = new Date(today.getTime() + (daysAhead * 24 * 60 * 60 * 1000));

    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        category:categories(id, name, emoji, color),
        payment_method:payment_methods(id, name, type, last_four)
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .not('next_billing_date', 'is', null)
      .gte('next_billing_date', today.toISOString().split('T')[0])
      .lte('next_billing_date', futureDate.toISOString().split('T')[0])
      .order('next_billing_date', { ascending: true });

    if (error) {
      console.error('Error fetching upcoming renewals:', error);
      throw error;
    }

    return data.map(transformSubscriptionFromDB);
  } catch (error) {
    console.error('getUpcomingRenewals error:', error);
    throw error;
  }
};

/**
 * Get subscriptions by category
 * @param {string} categoryId - Category ID
 * @returns {Promise<Array>} Array of subscriptions
 */
export const getSubscriptionsByCategory = async (categoryId) => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        category:categories(id, name, emoji, color),
        payment_method:payment_methods(id, name, type, last_four)
      `)
      .eq('user_id', userId)
      .eq('category_id', categoryId)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching subscriptions by category:', error);
      throw error;
    }

    return data.map(transformSubscriptionFromDB);
  } catch (error) {
    console.error('getSubscriptionsByCategory error:', error);
    throw error;
  }
};

/**
 * Calculate next billing date from last billing date and billing cycle
 * @param {string} lastBillingDate - Last billing date in YYYY-MM-DD format
 * @param {string} billingCycle - Billing cycle (weekly, monthly, quarterly, yearly)
 * @returns {string} Formatted next billing date (e.g., "Oct 20")
 */
const calculateNextBillingDisplay = (lastBillingDate, billingCycle) => {
  if (!lastBillingDate) return 'Not set';

  try {
    // Parse as local date (not UTC) to avoid timezone shift
    const [year, month, day] = lastBillingDate.split('-').map(Number);
    const lastDate = new Date(year, month - 1, day);
    const nextDate = new Date(lastDate);

    switch (billingCycle) {
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 7);
        break;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + 1);
        break;
      case 'quarterly':
        nextDate.setMonth(nextDate.getMonth() + 3);
        break;
      case 'yearly':
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        break;
      default:
        nextDate.setMonth(nextDate.getMonth() + 1);
    }

    return nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (error) {
    console.error('Error calculating next billing date:', error);
    return 'Not set';
  }
};

/**
 * Transform subscription data from database format to frontend format
 * @param {Object} dbSub - Subscription from database
 * @returns {Object} Transformed subscription
 */
const transformSubscriptionFromDB = (dbSub) => {
  return {
    id: dbSub.id,
    userId: dbSub.user_id,
    name: dbSub.name,
    service: dbSub.service,
    category: dbSub.category?.name || 'Uncategorized',
    categoryId: dbSub.category_id,
    price: parseFloat(dbSub.price),
    currency: dbSub.currency,
    billingCycle: dbSub.billing_cycle,
    status: dbSub.status,
    renewal: calculateNextBillingDisplay(dbSub.last_billing_date, dbSub.billing_cycle),
    nextBillingDate: dbSub.next_billing_date,
    subscriptionDate: dbSub.subscription_date,
    lastBillingDate: dbSub.last_billing_date,
    lastBillDate: dbSub.last_billing_date,
    description: dbSub.description,
    logo: dbSub.logo || '📋',
    color: dbSub.category?.color || 'bg-gray-500',
    url: dbSub.website_url,
    paymentMethod: dbSub.payment_method?.name || null,
    paymentMethodId: dbSub.payment_method_id,
    notes: dbSub.notes,
    isShared: dbSub.is_shared,
    sharedWith: dbSub.shared_with || [],
    tags: dbSub.tags || [],
    cancellationDate: dbSub.cancellation_date,
    trialEndDate: dbSub.trial_end_date,
    createdAt: dbSub.created_at,
    updatedAt: dbSub.updated_at,
    importSource: dbSub.import_source,
    importedFromEmail: dbSub.imported_from_email,
    originalEmailData: dbSub.original_email_data
  };
};

/**
 * Log a manual subscription status change as an event
 * @param {string} subscriptionId - The ID of the subscription
 * @param {string} eventType - 'cancellation', 'paused', 'reactivated'
 * @param {string} date - Date string in YYYY-MM-DD
 * @param {number} price - The price at the time of the event
 * @param {string} currency - The currency
 */
export const logSubscriptionEvent = async (subscriptionId, eventType, date, price, currency) => {
  try {
    const eventPayload = {
      subscription_id: subscriptionId,
      event_type: eventType,
      event_date: date ? new Date(date).toISOString() : new Date().toISOString(),
      price: price ? parseFloat(price) : 0,
      currency: currency || 'USD',
      notes: `User manually marked subscription as ${eventType}`
    };

    const { data, error } = await supabase
      .from('subscription_events')
      .insert([eventPayload])
      .select();

    if (error) {
      console.error('Error logging subscription event:', error);
      throw error;
    }

    return data[0];
  } catch (error) {
    console.error('logSubscriptionEvent error:', error);
    throw error;
  }
};

export default {
  getAllSubscriptions,
  getActiveSubscriptions,
  getPausedSubscriptions,
  getCancelledSubscriptions,
  getSubscriptionById,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  getUpcomingRenewals,
  getSubscriptionsByCategory
};
